import { memo } from "react";
import { ChevronDown, ChevronUp, Signal, ArrowLeft } from "lucide-react";

const STATUS_LABELS = { moving: "MOVING", idle: "IDLE", offline: "SIGNAL LOST", airborne: "AIRBORNE" };

// Roster shows only assets on the move — parked/idle ones stay off the list
// (they'd sit motionless, and any that parked somewhere odd would draw the eye).
const inRoster = (a) => a.status === "moving";
// The Gold Coast asset gets the gold treatment everywhere its call-sign shows.
const isGold = (a) => a && a.id === "agent-goldcoast";

/**
 * Two modes in one panel:
 *   - roster: scrollable list of active vehicles (default)
 *   - tracking: old-style detail card for the selected vehicle, with a
 *     back button that returns to the roster.
 */
const FleetPanelBase = ({
  agents,
  selectedId,
  onSelect,
  follow,
  setFollow,
  open,
  onToggle,
}) => {
  const all = agents || [];
  const list = all
    .filter(inRoster)
    .sort((x, y) => (x.code || "").localeCompare(y.code || ""));
  const selected = all.find((a) => a.id === selectedId) || null;

  return (
    <div className={`tracker-panel ${open ? "open" : "collapsed"}`} data-testid="fleet-panel">
      <div className="tracker-header" onClick={onToggle}>
        <span>{selected ? `TRACKING · ${selected.code}` : `ASSETS · ${list.length}`}</span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </div>

      {open && (
        <div className="tracker-body">
          {selected ? (
            <div className="agent-card" data-testid="agent-card">
              <button
                className="fleet-back"
                onClick={() => onSelect(null)}
                data-testid="stop-tracking"
                title="Back to all assets"
              >
                <ArrowLeft size={12} /> ALL ASSETS
              </button>

              <div className="agent-card-row">
                <span className={`agent-code-big ${isGold(selected) ? "gold" : ""}`}>{selected.code}</span>
                <button
                  className={`follow-btn ${follow ? "active" : ""}`}
                  onClick={() => setFollow(!follow)}
                  data-testid="follow-btn"
                  title={follow ? "Unlock camera" : "Lock camera on asset"}
                >
                  <Signal size={12} />
                </button>
              </div>

              <div className="agent-status-row">
                <span className={`status-pill ${selected.signal_searching ? "offline" : selected.drone ? "airborne" : selected.status}`} data-testid="agent-status">
                  {selected.signal_searching && <span className="pulse-dot-red" />}
                  {selected.signal_searching
                    ? "SIGNAL LOST"
                    : selected.drone
                    ? "AIRBORNE"
                    : STATUS_LABELS[selected.status] || selected.status.toUpperCase()}
                </span>
              </div>

              <div className="agent-meta">
                <div>SPEED: <strong data-testid="agent-speed">{(selected.speed || 0).toFixed(1)} km/h</strong></div>
              </div>

              {/* Golden entity: battery telemetry. No explanation offered. */}
              {typeof selected.battery === "number" && (
                <div className="battery-row" data-testid="battery-row">
                  <span className="battery-label">PWR</span>
                  <div className="battery-track">
                    <div
                      className={`battery-fill ${selected.battery <= 20 ? "low" : selected.battery <= 45 ? "mid" : ""}`}
                      style={{ width: `${selected.battery}%` }}
                    />
                  </div>
                  <span className="battery-pct">{selected.battery}%</span>
                </div>
              )}

              {selected.signal_searching && (
                <div className="agent-searching-note">Asset last seen in this vicinity.</div>
              )}
            </div>
          ) : list.length === 0 ? (
            <div className="empty-state">No active signals…</div>
          ) : (
            <div className="fleet-list" data-testid="fleet-list">
              {list.map((a) => {
                const st = a.drone ? "airborne" : a.status === "moving" ? "moving" : "idle";
                return (
                  <button
                    key={a.id}
                    className={`fleet-row ${a.id === selectedId ? "selected" : ""}`}
                    onClick={() => onSelect(a.id)}
                    data-testid={`fleet-row-${a.id}`}
                    title={a.code}
                  >
                    <span className={`fleet-code ${isGold(a) ? "gold" : ""}`}>{a.code}</span>
                    <span className={`fleet-status ${st}`}>{STATUS_LABELS[st]}</span>
                    <span className="fleet-speed">{Math.round(a.speed || 0)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const FleetPanel = memo(FleetPanelBase);
